// Detects Bible references in arbitrary text (e.g. "I was meditating on
// Romans 8:1 this week"). Used to link encouragements to verses without
// relying on Claude's scan or the encouragement_refs table being present.

const BOOK_NAMES = [
  // Old Testament
  'Genesis','Exodus','Leviticus','Numbers','Deuteronomy','Joshua','Judges','Ruth',
  '1 Samuel','2 Samuel','1 Kings','2 Kings','1 Chronicles','2 Chronicles',
  'Ezra','Nehemiah','Esther','Job','Psalms','Proverbs','Ecclesiastes',
  'Song of Solomon','Isaiah','Jeremiah','Lamentations','Ezekiel','Daniel',
  'Hosea','Joel','Amos','Obadiah','Jonah','Micah','Nahum','Habakkuk',
  'Zephaniah','Haggai','Zechariah','Malachi',
  // New Testament
  'Matthew','Mark','Luke','John','Acts','Romans',
  '1 Corinthians','2 Corinthians','Galatians','Ephesians','Philippians','Colossians',
  '1 Thessalonians','2 Thessalonians','1 Timothy','2 Timothy','Titus','Philemon',
  'Hebrews','James','1 Peter','2 Peter','1 John','2 John','3 John','Jude','Revelation',
]

// Common alternate spellings → canonical book name
const ALIASES = {
  'Psalm':           'Psalms',
  'Song of Songs':   'Song of Solomon',
  'Canticles':       'Song of Solomon',
}

// Build a regex that catches "Book[ alias] Chapter:Verse". Books are sorted
// longest-first so e.g. "1 John" wins over "John".
const BOOK_REGEX_SRC = (() => {
  const names = [...BOOK_NAMES, ...Object.keys(ALIASES)]
    .sort((a, b) => b.length - a.length)
    .map(n => n.replace(/\s+/g, '\\s+'))
  return `(${names.join('|')})\\s+(\\d{1,3}):(\\d{1,3})`
})()

export function parseBibleRefs(text) {
  if (!text) return []
  const rx = new RegExp(BOOK_REGEX_SRC, 'gi')
  const out = []
  const seen = new Set()
  let m
  while ((m = rx.exec(text)) !== null) {
    const rawBook = m[1].replace(/\s+/g, ' ')
    const canonical = canonicalizeBook(rawBook)
    if (!canonical) continue
    const chapter = parseInt(m[2], 10)
    const verse   = parseInt(m[3], 10)
    const verseRef = `${canonical} ${chapter}:${verse}`
    if (seen.has(verseRef)) continue
    seen.add(verseRef)
    out.push({ verseRef, book: canonical, chapter, verse })
  }
  return out
}

// Map any spelling (case-insensitive, with aliases) to the BOOK_NAMES form.
// Returns null if unknown.
export function canonicalizeBook(raw) {
  if (!raw) return null
  const norm = String(raw).replace(/\s+/g, ' ').trim().toLowerCase()
  const direct = BOOK_NAMES.find(b => b.toLowerCase() === norm)
  if (direct) return direct
  const aliasKey = Object.keys(ALIASES).find(k => k.toLowerCase() === norm)
  return aliasKey ? ALIASES[aliasKey] : null
}

// Given a stored verseRef like "Psalm 14:3", "Psalm 103:1-22", or "Psalm 14"
// (chapter only), return { verseKeys, chapterKey } where verseKeys is an
// array of canonical "Book Chapter:Verse" strings and chapterKey, if set, is
// a canonical "Book Chapter" string for a whole-chapter reference.
export function expandVerseRef(verseRef) {
  if (!verseRef) return { verseKeys: [], chapterKey: null }
  const m = String(verseRef).match(/^\s*(.+?)\s+(\d+)(?::(\d+)(?:[-–](\d+))?)?\s*$/)
  if (!m) return { verseKeys: [], chapterKey: null }
  const book = canonicalizeBook(m[1])
  if (!book) return { verseKeys: [], chapterKey: null }
  const chapter = parseInt(m[2], 10)
  if (!m[3]) {
    // Chapter-only reference - links to the whole chapter.
    return { verseKeys: [], chapterKey: `${book} ${chapter}` }
  }
  const v1 = parseInt(m[3], 10)
  const v2 = m[4] ? parseInt(m[4], 10) : v1
  const verseKeys = []
  for (let v = Math.min(v1, v2); v <= Math.max(v1, v2); v++) {
    verseKeys.push(`${book} ${chapter}:${v}`)
  }
  return { verseKeys, chapterKey: null }
}
